import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import CountryCodeSelect from "../CountryCodeSelect";

const filter = createFilterOptions({
	trim: true,
});

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "20px 24px 12px",
	},
	titleText: {
		color: "#1E9855",
		fontWeight: 600,
		fontSize: 20,
	},
	content: {
		padding: "16px 24px 24px",
		display: "flex",
		flexDirection: "column",
		gap: 20,
		minWidth: 420,
	},
	formControl: {
		"& .MuiOutlinedInput-root": {
			borderRadius: 8,
		},
	},
	phoneRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	phoneInput: {
		flex: 1,
	},
	actionButton: {
		borderRadius: 24,
		padding: "10px 28px",
		textTransform: "none",
		fontWeight: 600,
		fontSize: 15,
	},
	errorText: {
		color: "red",
		fontSize: 12,
		marginTop: 2,
	},
	dividerText: {
		textAlign: "center",
		margin: "4px 0",
		color: theme.palette.text.secondary,
		fontSize: 13,
	},
}));

const NewTicketModal = ({ modalOpen, onClose }) => {
	const classes = useStyles();
	const history = useHistory();

	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);

	const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
	const [selectedCountry, setSelectedCountry] = useState({ name: "Brasil", code: "55", iso: "BR", flag: "🇧🇷" });
	const [phoneNumber, setPhoneNumber] = useState("");
	const [contactName, setContactName] = useState("");
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (modalOpen && whatsApps && whatsApps.length === 1) {
			setSelectedWhatsapp(whatsApps[0].id);
		}
	}, [modalOpen, whatsApps]);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setPhoneNumber("");
		setContactName("");
		setSelectedWhatsapp("");
		setSelectedCountry({ name: "Brasil", code: "55", iso: "BR", flag: "🇧🇷" });
		setErrors({});
	};

	const handleSaveTicket = async (contactId) => {
		if (!contactId) return;
		setLoading(true);
		try {
			const { data: ticket } = await api.post("/tickets", {
				contactId: contactId,
				userId: user.id,
				status: "open",
			});
			history.push(`/atendimentos/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
		handleClose();
	};

	const handleStartNewChat = async () => {
		const newErrors = {};
		if (!selectedWhatsapp) newErrors.whatsapp = "Obrigatório";
		if (!phoneNumber.trim()) newErrors.phone = "Obrigatório";
		if (!contactName.trim()) newErrors.name = "Obrigatório";

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setErrors({});
		setLoading(true);

		try {
			const fullNumber = selectedCountry.code + phoneNumber.replace(/\D/g, "");
			let contactId;

			try {
				const { data: contactData } = await api.post("/contacts", {
					name: contactName,
					number: fullNumber,
				});
				contactId = contactData.id;
			} catch (err) {
				
				const { data: searchData } = await api.get("/contacts", {
					params: { searchParam: fullNumber },
				});
				if (searchData.contacts && searchData.contacts.length > 0) {
					contactId = searchData.contacts[0].id;
				} else {
					toastError(err);
					setLoading(false);
					return;
				}
			}

			const { data: ticket } = await api.post("/tickets", {
				contactId: contactId,
				userId: user.id,
				status: "open",
				whatsappId: selectedWhatsapp,
			});
			history.push(`/atendimentos/${ticket.id}`);
			handleClose();
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	const handleSelectOption = (e, newValue) => {
		if (newValue?.number) {
			setSelectedContact(newValue);
		} else if (newValue?.name) {
			setNewContact({ name: newValue.name });
			setContactModalOpen(true);
		}
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddNewContactTicket = (contact) => {
		handleSaveTicket(contact.id);
	};

	const createAddContactOption = (filterOptions, params) => {
		const filtered = filter(filterOptions, params);

		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({
				name: `${params.inputValue}`,
			});
		}

		return filtered;
	};

	const renderOption = (option) => {
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${i18n.t("newTicketModal.add")} ${option.name}`;
		}
	};

	const renderOptionLabel = (option) => {
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${option.name}`;
		}
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
			></ContactModal>
			<Dialog
				open={modalOpen}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				PaperProps={{
					style: { borderRadius: 16 },
				}}
			>
				<div className={classes.dialogTitle}>
					<Typography className={classes.titleText}>
						Iniciar novo chat
					</Typography>
					<IconButton size="small" onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				</div>

				<DialogContent className={classes.content}>
					{}
					<FormControl
						variant="outlined"
						fullWidth
						required
						error={!!errors.whatsapp}
						className={classes.formControl}
					>
						<InputLabel>Conexão</InputLabel>
						<Select
							label="Conexão *"
							value={selectedWhatsapp}
							onChange={(e) => {
								setSelectedWhatsapp(e.target.value);
								setErrors((prev) => ({ ...prev, whatsapp: undefined }));
							}}
						>
							<MenuItem value="">
								<em>Selecionar uma conexão</em>
							</MenuItem>
							{whatsApps && whatsApps.map((w) => (
								<MenuItem key={w.id} value={w.id} style={{ display: "flex", alignItems: "center" }}>
									{w.name}
									{w.status === "CONNECTED" && (
										<CheckCircleIcon style={{ color: "#1E9855", fontSize: 16, marginLeft: 6 }} />
									)}
									{w.status !== "CONNECTED" && ` (${w.status})`}
								</MenuItem>
							))}
						</Select>
						{errors.whatsapp && (
							<Typography className={classes.errorText}>
								{errors.whatsapp}
							</Typography>
						)}
					</FormControl>

					{}
					<div>
						<Typography variant="body2" style={{ marginBottom: 8, fontWeight: 500 }}>
							Telefone <span style={{ color: "red" }}>*</span>
						</Typography>
						<div className={classes.phoneRow}>
							<CountryCodeSelect
								value={selectedCountry.iso}
								onChange={(country) => setSelectedCountry(country)}
							/>

							<TextField
								className={classes.phoneInput}
								variant="outlined"
								size="small"
								placeholder="(00) 00000-0000"
								value={phoneNumber}
								onChange={(e) => {
									setPhoneNumber(e.target.value);
									setErrors((prev) => ({ ...prev, phone: undefined }));
								}}
								error={!!errors.phone}
								InputProps={{
									style: { borderRadius: 8 },
								}}
							/>
						</div>
						{errors.phone && (
							<Typography className={classes.errorText}>
								{errors.phone}
							</Typography>
						)}
					</div>

					{}
					<TextField
						label="Nome"
						variant="outlined"
						fullWidth
						required
						value={contactName}
						onChange={(e) => {
							setContactName(e.target.value);
							setErrors((prev) => ({ ...prev, name: undefined }));
						}}
						error={!!errors.name}
						helperText={errors.name}
						InputProps={{
							style: { borderRadius: 8 },
						}}
					/>
				</DialogContent>

				<DialogActions style={{ padding: "16px 24px", justifyContent: "flex-end" }}>
					<ButtonWithSpinner
						variant="contained"
						color="primary"
						className={classes.actionButton}
						onClick={handleStartNewChat}
						loading={loading}
					>
						Iniciar Atendimento
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewTicketModal;
